import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useTraces } from "@/lib/hooks/useTraces";
import { groupSpansByTrace } from "@/utils/trace-utils";
import { AlertCircle, RefreshCw } from "lucide-react";
import type { DockmasterSpan } from "../../../../../../schemas/codegen";
import { ExpandableTraceTable } from "./ExpandableTraceTable";
import type { EnhancedTrace } from "./types";

export function TraceViewer() {
  const projectId = useActiveProjectId();
  const { data, isLoading, error } = useTraces(projectId ?? "");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading traces...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="w-4 h-4" />
          Error loading traces: {error.message}
        </div>
      </div>
    );
  }

  const tracesData = data?.traces ?? [];

  // Process traces using utility functions
  const enhancedTraces: EnhancedTrace[] = tracesData.map((trace) => {
    if (!trace.spans || trace.spans.length === 0) {
      return {
        traceId: trace.traceId,
        startTime: 0,
        endTime: 0,
        duration: 0,
        spanCount: 0,
        hasErrors: false,
        spans: [],
      };
    }

    // Use utility to process the trace (cast spans to correct type)
    const [processedTrace] = groupSpansByTrace(trace.spans as DockmasterSpan[]);
    return (
      processedTrace || {
        traceId: trace.traceId,
        startTime: 0,
        endTime: 0,
        duration: 0,
        spanCount: 0,
        hasErrors: false,
        spans: [],
      }
    );
  });

  // Show expandable table with integrated details
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b p-4 bg-background/50 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground">MCP Traces</h3>
            <p className="text-xs text-muted-foreground">
              Expandable table view of this project's MCP server operations and
              tool calls.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            {enhancedTraces.length} trace
            {enhancedTraces.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <ExpandableTraceTable traces={enhancedTraces} />
      </div>
    </div>
  );
}
